import React, { useEffect } from 'react';
import { merge } from 'lodash';
import { Flex, Text, styled, css, Box } from '@goodrx/matisse';
import { useImmer } from 'app/hooks';
import { isClient, mediaMinWidth } from 'app/lib/utils';
import { startSession } from 'app/lib/infermedica';
import { TriageBlockCodes, TriageBlocks, TriageDatabase } from './blocks';
import { TriageBlock, TriageBlockConfig } from './TriageBlock';

interface TriageCache {
  cursor: number;
  conversationCodes: TriageBlockCodes[];
  dataValues: TriageDatabase;
  inputValues: Record<TriageBlockCodes, any>;
  sessionId: string;
}

const getBlockCode = (block: TriageBlockConfig, index: number): TriageBlockCodes | string =>
  block.codePostfix ? `${block.code}_${index}` : block.code;

const createTriageBlock = (blockCode: TriageBlockCodes) => TriageBlocks[blockCode];

const ChatContainer = styled(Flex)`
  flex-direction: column;
  max-width: 580px;
  width: 100%;

  ${mediaMinWidth({
    sm: css`
      top: 0;
      height: 80vh;
      border: 1px solid ${(p) => p.theme.colors.greyLight};
      border-radius: 12px;
      width: 580px;
    `,
  })}
`;

const ContainerHeader = styled(Flex)(
  (p) => css`
    height: 36px;
    align-items: center;
    padding: ${p.theme.space[3]}px;
    border-bottom: 1px solid ${p.theme.colors.greyLight};
    width: 100%;
    font-size: 14px;

    ${mediaMinWidth({
      sm: css`
        border-radius: 12px;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      `,
    })}
  `
);

const Chat = styled(Flex)`
  overflow-y: auto;
  flex-direction: column;
  height: 100%;

  ${mediaMinWidth({
    sm: css`
      height: 50vh;
    `,
  })}
`;

export const TriageManager: React.FC = () => {
  const cache: TriageCache =
    isClient() && sessionStorage.getItem('triageCache')
      ? JSON.parse(sessionStorage.getItem('triageCache') as string)
      : {};

  useEffect(() => {
    if (!cache.sessionId) {
      startSession().then(({ sessionId: id }) => setState({ sessionId: id }));
    }
  }, [cache.sessionId]);

  const initialConversationCodes: Array<TriageBlockCodes> = cache.conversationCodes
    ? cache.conversationCodes
    : ['agreeToTerms'];

  const initialTriageBlocks = initialConversationCodes.map((code) => createTriageBlock(code));

  const defaultDataValues = {
    evidence: {},
  } as TriageDatabase;

  const [{ conversation, cursor, dataValues, inputValues, sessionId }, setState] = useImmer({
    conversation: initialTriageBlocks,
    cursor: Number(cache.cursor) || 0,
    dataValues: cache.dataValues ? cache.dataValues : { ...defaultDataValues },
    inputValues: cache.inputValues ? cache.inputValues : {},
    // This is a way to group patient analytics around a triage "session"
    sessionId: cache.sessionId,
  });

  const previousConvoBlocks = conversation.slice(0, cursor);
  const currentConvoBlock = conversation?.[cursor];
  const currentConvoBlockCode = getBlockCode(currentConvoBlock, cursor);

  const setDataValue = (key: string, value: any) =>
    setState((draft) => {
      draft.dataValues[key] = merge({}, draft.dataValues[key], value);
    });

  const setInputValue = (key: string, value: any) =>
    setState((draft) => {
      draft.inputValues[key] = value;
    });

  const setCurrentInputValue = <T,>(value: T) => setInputValue(currentConvoBlockCode, value);

  const setCurrentDataValue = <T,>(value: T) => setDataValue(currentConvoBlockCode, value);

  useEffect(() => {
    const nextCache = {
      cursor,
      conversationCodes: conversation.map((block) => block.code),
      inputValues,
      dataValues,
      sessionId,
    };

    sessionStorage.setItem('triageCache', JSON.stringify(nextCache));
  }, [dataValues, inputValues, cursor, conversation, sessionId]);

  const removeBlock = () =>
    setState((draft) => {
      // Wipe out values collected for block we're leaving
      draft.inputValues[currentConvoBlockCode] = undefined;
      draft.dataValues[currentConvoBlockCode] = undefined;
      draft.dataValues.evidence[currentConvoBlockCode] = undefined;

      draft.cursor -= 1;
      draft.conversation = draft.conversation.slice(0, -1);
      const nextConvoBlock = draft.conversation[draft.cursor];
      const nextConvoBlockCode = getBlockCode(nextConvoBlock, draft.cursor);

      // Wipe out previous evidence collected for newly active block
      draft.dataValues.evidence[nextConvoBlockCode] = undefined;

      if (nextConvoBlockCode !== 'chiefComplaint') {
        // Wipe out the previously entered values for the previous block except for initia symptoms
        draft.inputValues[nextConvoBlockCode] = undefined;
      }
    });

  const replaceBlock = (blockCode: TriageBlockCodes, blockData: any) =>
    setState((draft) => {
      // Wipe out values collected for block we're leaving
      draft.inputValues[currentConvoBlockCode] = undefined;
      draft.dataValues[currentConvoBlockCode] = undefined;
      draft.dataValues.evidence[currentConvoBlockCode] = undefined;
      draft.conversation = draft.conversation.slice(0, -1);
      draft.conversation.push(TriageBlocks[blockCode]);
      setDataValue(blockCode, blockData);
    });

  function addBlock(blockCode: TriageBlockCodes, blockData: any) {
    setState((draft) => {
      const nextBlock = createTriageBlock(blockCode);
      draft.cursor += 1;
      draft.conversation.push(nextBlock);
      setDataValue(getBlockCode(nextBlock, draft.cursor), blockData);
    });
  }

  const sharedProps = {
    cursor,
    dataValues,
    setCurrentDataValue,
    setCurrentInputValue,
    setDataValue,
    setInputValue,
    addBlock,
    removeBlock,
    replaceBlock,
    sessionId,
  };

  return (
    <Box position="relative">
      <ChatContainer mt={[-4, -4, 0]} mx={[0, 0, 'auto']}>
        <ContainerHeader>
          <Text role="img" aria-label="Robot Emoji" mr={2} mb={1}>
            🤖
          </Text>
          <Text>
            Chatting with <strong>HeyDoctor Assistant</strong>
          </Text>
        </ContainerHeader>

        <Chat flexDirection="column" p={3} flex={1}>
          {previousConvoBlocks.map((block, index) => {
            const blockCode = getBlockCode(block, index);
            return (
              <TriageBlock
                key={blockCode}
                blockCode={blockCode}
                dataValue={dataValues[blockCode]}
                inputValue={inputValues[blockCode]}
                isPrevious={index === previousConvoBlocks.length - 1}
                {...sharedProps}
                {...block}
              />
            );
          })}

          <TriageBlock
            key={currentConvoBlockCode}
            blockCode={currentConvoBlockCode}
            isCurrent
            dataValue={dataValues[currentConvoBlockCode]}
            inputValue={inputValues[currentConvoBlockCode]}
            {...sharedProps}
            {...currentConvoBlock}
          />
        </Chat>

        <Flex p={3} alignItems="center">
          <img src="/images/infermedica.svg" alt="Infermedica Logo" width={16} height={16} />
          <Text fontWeight={500} fontSize={14} ml={2} color="greyDarkest">
            Powered by Infermedica
          </Text>
        </Flex>
      </ChatContainer>
    </Box>
  );
};
